import { useState } from "react";

import { Input } from "@/components/ui/input";
import { parseTypedDay } from "@/lib/calendar-dates";

/**
 * The typed way into a date picker: a day as text, taken on Enter or on
 * leaving the field.
 *
 * Not on every keystroke — `2026-07-1` is on its way to being a date, and a
 * picker that acted on it would move the calendar out from under the typing.
 * Text that is not a day, or is one the picker does not allow, is marked and
 * changes nothing.
 */
export function DayField({
  label,
  value,
  accepts,
  onCommit,
}: {
  label: string;
  value: string;
  accepts?: (day: string) => boolean;
  onCommit: (day: string) => void;
}) {
  const [text, setText] = useState(value);
  // The value the text was last in step with: a day picked from the grid
  // replaces whatever was half-typed.
  const [seen, setSeen] = useState(value);
  const [invalid, setInvalid] = useState(false);

  if (seen !== value) {
    setSeen(value);
    setText(value);
    setInvalid(false);
  }

  function commit() {
    if (text.trim() === "" || text === value) {
      setInvalid(false);
      return;
    }
    const day = parseTypedDay(text);
    if (day === undefined || (accepts && !accepts(day))) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onCommit(day);
  }

  return (
    <Input
      aria-label={label}
      aria-invalid={invalid}
      data-slot="day-field"
      inputMode="numeric"
      placeholder="YYYY-MM-DD"
      className="h-8 min-w-0 flex-1 tabular-nums"
      value={text}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit();
        }
      }}
    />
  );
}
